import { useTranslation } from 'react-i18next';

import {
    Checkbox,
    DateOfBirthField,
    FormFieldError,
    Stack,
    TextInput,
} from '@myphx-engineering/phx-design-system';
import { type FormErrors } from '@myphx-engineering/phx-design-system/hooks';

import { type VerifyFormDetails, useVerifyFormContext } from './verify-form';

export type VerifyFormProps = {
    onError?: (errors: FormErrors<VerifyFormDetails>) => void;
    onSubmit: (data: VerifyFormDetails) => Promise<void>;
    onFormValueChange?: (isValid: boolean) => void;
};

export const VerifyForm = (props: VerifyFormProps) => {
    const { onSubmit, onError, onFormValueChange } = props;
    const { t } = useTranslation();
    const form = useVerifyFormContext();

    // These errors get aggregated under one "name" error
    const { error: _firstNameError, ...firstNameProps } =
        form.getInputProps('firstName');
    const { error: _lastNameError, ...lastNameProps } =
        form.getInputProps('lastName');

    onFormValueChange?.(form.isValid());

    return (
        <form
            onSubmit={form.onSubmit(onSubmit, onError)}
            id="verify-prescription"
            data-testid="verify-patient"
        >
            <Stack gap="md">
                <TextInput
                    key={form.key('firstName')}
                    name="firstName"
                    label={t('common.formFields.firstName')}
                    error={!!form.errors.name}
                    required
                    {...firstNameProps}
                />
                <TextInput
                    key={form.key('lastName')}
                    name="lastName"
                    label={t('common.formFields.lastName')}
                    error={!!form.errors.name}
                    required
                    {...lastNameProps}
                />
                {form.errors.name && typeof form.errors.name === 'string' && (
                    <FormFieldError errText={form.errors.name} />
                )}
                <DateOfBirthField
                    labels={{
                        heading: t('common.formFields.dob'),
                        month: t('common.formFields.dobMonth'),
                        day: t('common.formFields.dobDay'),
                        year: t('common.formFields.dobYear'),
                    }}
                    monthProps={form.getInputProps('dobMonth')}
                    dayProps={form.getInputProps('dobDay')}
                    yearProps={form.getInputProps('dobYear')}
                    groupError={form.errors.dobDate}
                    onGroupValidation={(error) => {
                        if (error) {
                            form.setFieldError('dobDate', error);
                        } else {
                            form.clearFieldError('dobDate');
                        }
                    }}
                />
                <Checkbox
                    label={t('verifyPrescription.formFields.attest')}
                    {...form.getInputProps('attest')}
                    error={null}
                />
            </Stack>
        </form>
    );
};
